import { useEffect, useState } from 'react';
import { supabase } from '../../lib/supabase';
import { registerModalBackHandler } from '../../lib/native';
import { bankCsv, downloadCsv, proofFileError } from '../../lib/invoiceExports';
import { formatMoney, safeFileName } from '../../lib/hostInvoice';
import { notifyInvoiceEvent } from '../../lib/invoiceNotify';
import { Button, Input } from '../design-system';
import type { AdminHostInvoice } from '../../types';

interface PayoutRunPanelProps {
  /** The approved invoices ticked for this run. */
  invoices: AdminHostInvoice[];
  adminId: string;
  onClear: () => void;
  /** Called after the run is recorded, so the list can refresh. */
  onDone: () => void;
}

function todayISO(): string {
  const d = new Date();
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  return d.toISOString().slice(0, 10);
}

/**
 * Pay the ticked invoices: download the bank file, make the transfers at the bank,
 * then record the run here with its date, the bank reference and the bank's
 * confirmation. Recording marks every invoice and its trips as paid and tells
 * the hosts.
 */
export function PayoutRunPanel({ invoices, adminId, onClear, onDone }: PayoutRunPanelProps) {
  const [runDate, setRunDate] = useState(todayISO());
  const [reference, setReference] = useState('');
  const [proof, setProof] = useState<File | null>(null);
  const [showErrors, setShowErrors] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!confirming) return;
    return registerModalBackHandler(() => { if (!busy) setConfirming(false); });
  }, [confirming, busy]);

  const currencies = new Set(invoices.map((i) => i.currency));
  const currency = invoices[0]?.currency ?? 'eur';
  const totalCents = invoices.reduce((n, i) => n + i.invoice_payable_cents, 0);
  const hosts = new Set(invoices.map((i) => i.host_id)).size;

  const refError = reference.trim().length < 1 ? 'Enter the bank reference of the payment.' : reference.trim().length > 100 ? 'The reference is too long.' : null;
  const dateError = !/^\d{4}-\d{2}-\d{2}$/.test(runDate) || runDate > todayISO() ? 'Enter the date of the payment (not in the future).' : null;
  const fileError = proofFileError(proof ? { type: proof.type, size: proof.size } : null);
  const mixed = currencies.size > 1;

  function download() {
    downloadCsv(`sekretly-payments-${todayISO()}.csv`, bankCsv(invoices));
  }

  function review() {
    setShowErrors(true);
    setError(null);
    if (refError || dateError || fileError || mixed) return;
    setConfirming(true);
  }

  async function record() {
    if (!proof) return;
    setBusy(true);
    setError(null);
    const path = `${adminId}/${crypto.randomUUID()}-${safeFileName(proof.name)}`;
    const upload = await supabase.storage.from('payout-proofs').upload(path, proof, { contentType: proof.type, upsert: false });
    if (upload.error) {
      console.error('proof upload failed:', upload.error);
      setBusy(false);
      setConfirming(false);
      setError('We couldn’t upload the proof of payment. Check your connection and try again.');
      return;
    }
    const { error: e } = await supabase.rpc('create_payout_run', {
      p_invoice_ids: invoices.map((i) => i.id),
      p_run_date: runDate,
      p_bank_reference: reference.trim(),
      p_proof_path: path,
    });
    setBusy(false);
    setConfirming(false);
    if (e) { setError(e.message); return; }
    await Promise.all(invoices.map((i) => notifyInvoiceEvent(i.id, 'paid')));
    onDone();
  }

  return (
    <section className="rounded-2xl border border-gold/30 bg-gold/8 p-4 md:p-5 flex flex-col gap-4 mb-5" aria-label="Payout run">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="text-sm font-semibold text-app-fg">Pay {invoices.length} invoice{invoices.length === 1 ? '' : 's'}</h2>
          <p className="text-xs text-app-muted mt-0.5">
            {formatMoney(totalCents, currency)} to {hosts} host{hosts === 1 ? '' : 's'}
          </p>
        </div>
        <button type="button" onClick={onClear} className="text-xs text-app-muted underline underline-offset-2">Clear</button>
      </div>

      {mixed ? <p role="alert" className="text-sm text-crimson">These invoices are in different currencies. A run can only cover one.</p> : null}

      <div className="flex flex-col gap-1.5">
        <p className="text-xs text-app-muted">1. Download the bank file and make the transfers at the bank.</p>
        <div><Button variant="secondary" size="sm" onClick={download} disabled={mixed}>Download bank file (CSV)</Button></div>
      </div>

      <div className="flex flex-col gap-3">
        <p className="text-xs text-app-muted">2. Record the payment here, with the bank’s confirmation.</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Input label="Date of payment" type="date" value={runDate} onChange={(e) => setRunDate(e.target.value)} error={showErrors ? dateError ?? undefined : undefined} disabled={busy} />
          <Input label="Bank reference" value={reference} onChange={(e) => setReference(e.target.value)} error={showErrors ? refError ?? undefined : undefined} disabled={busy} />
        </div>
        <div className="flex flex-col gap-1.5">
          <label htmlFor="run-proof" className="text-[13px] font-medium text-app-fg-2">Proof of payment (PDF, JPG or PNG, up to 10 MB)</label>
          <input id="run-proof" type="file" accept="application/pdf,image/jpeg,image/png" disabled={busy}
            onChange={(e) => setProof(e.target.files?.[0] ?? null)} className="text-sm text-app-fg" />
          {showErrors && fileError ? <p className="text-[11px] text-crimson">{fileError}</p> : null}
        </div>
        {error ? <p role="alert" className="text-sm text-crimson">{error}</p> : null}
        <div><Button onClick={review} disabled={busy}>Record payment</Button></div>
      </div>

      {confirming && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={() => { if (!busy) setConfirming(false); }}>
          <div role="dialog" aria-label="Confirm payment" className="bg-app-bg border border-app-border rounded-2xl p-5 max-w-sm w-full flex flex-col gap-4" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-base font-semibold text-app-fg">Record this payment?</h3>
            <p className="text-sm text-app-muted leading-relaxed">
              {formatMoney(totalCents, currency)} was paid to {hosts} host{hosts === 1 ? '' : 's'} on {runDate}, reference {reference.trim()}.
              This marks {invoices.length} invoice{invoices.length === 1 ? '' : 's'} and their trips as paid and tells the hosts. It can’t be undone here.
            </p>
            <div className="flex gap-2">
              <Button onClick={() => void record()} disabled={busy}>{busy ? 'Recording…' : 'Yes, record it'}</Button>
              <Button variant="secondary" onClick={() => setConfirming(false)} disabled={busy}>Cancel</Button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

export default PayoutRunPanel;
